<template>
  <div>
    <!-- Title -->
    <div
      v-if="title"
      class="text-center text-subtitle-1 font-weight-medium py-2 grey--text text--darken-1 mb-2"
    >
      {{ title }}
    </div>

    <!-- Search and Sort filters -->
    <v-row v-if="showFilters" class="mb-0 px-2">
      <v-col cols="6" class="pt-0">
        <text-field
          v-model="internalSearch"
          kind="search"
          :label="$t('search')"
          hide-details
        />
      </v-col>
      <v-col cols="6" class="pt-0">
        <autocomplete
          v-model="internalSortBy"
          :items="sortOptions"
          :label="$t('sort_by')"
        />
      </v-col>
    </v-row>

    <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>
    <div v-else-if="processedItems.length > 0" :class="containerClass">
      <!-- Table mode: all fields fit in one row -->
      <template v-if="isTableMode">
        <!-- Header row -->
        <v-row no-gutters align="center" class="grey lighten-4">
          <v-col
            v-for="header in tableHeaders"
            :key="header.value"
            :cols="header.cols || getFieldCols(fieldsPerRow)"
          >
            <v-list-item dense class="py-0">
              <v-list-item-content class="py-1">
                <v-list-item-title class="text-caption font-weight-medium grey--text text--darken-1">
                  {{ header.text }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <!-- Empty header for actions -->
          <v-col v-if="hasActionsSlot" cols="auto">
            <v-list-item dense class="py-0">
              <v-list-item-content class="py-1">
                <v-list-item-title class="text-caption font-weight-medium grey--text text--darken-1">
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-col>
        </v-row>
        <v-divider></v-divider>

        <!-- Data rows -->
        <div
          v-for="(item, index) in processedItems"
          :key="getItemKey(item, index)"
          @click="$emit('click:row', item)"
        >
          <v-row no-gutters align="center">
            <v-col
              v-for="field in getFieldRows(item, index)[0]"
              :key="field.value"
              :cols="field.cols || getFieldCols(fieldsPerRow)"
            >
              <v-list-item dense class="py-0">
                <v-list-item-content class="py-1">
                  <v-list-item-title :class="field.class">
                    <slot :name="`item.${field.value}`" :item="item" :index="index">
                      {{ field.displayValue }}
                    </slot>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <!-- Actions in table row -->
            <v-col v-if="hasActionsSlot" cols="auto">
              <slot name="item.actions" :item="item" :index="index"></slot>
            </v-col>
          </v-row>
          <v-divider></v-divider>
        </div>
      </template>

      <!-- Card mode: multiple rows per item -->
      <template v-else>
        <v-card
          v-for="(item, index) in processedItems"
          :key="getItemKey(item, index)"
          class="mb-3 ordera-box-shadow"
          outlined
          @click="$emit('click:row', item)"
        >
          <!-- Fields grouped in rows -->
          <template v-for="(row, rowIndex) in getFieldRows(item, index)">
            <v-row no-gutters>
              <v-col
                v-for="(field, fieldIndex) in row"
                :key="field.value"
                :cols="field.cols || getFieldCols(row.length)"
              >
                <v-list-item dense>
                  <v-list-item-content>
                    <v-list-item-subtitle
                      v-if="!hideLabels"
                      class="text-caption grey--text"
                    >
                      {{ field.text }}
                    </v-list-item-subtitle>
                    <v-list-item-title :class="field.class">
                      <slot :name="`item.${field.value}`" :item="item" :index="index">
                        {{ field.displayValue }}
                      </slot>
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action v-if="$scopedSlots[`item.${field.value}.action`]">
                    <slot
                      :name="`item.${field.value}.action`"
                      :item="item"
                      :index="index"
                    ></slot>
                  </v-list-item-action>
                </v-list-item>
              </v-col>
            </v-row>
            <v-divider v-if="rowIndex < getFieldRows(item, index).length - 1"></v-divider>
          </template>

          <!-- Actions slot -->
          <template v-if="hasActionsSlot">
            <v-divider></v-divider>
            <v-card-actions>
              <slot name="item.actions" :item="item" :index="index"></slot>
            </v-card-actions>
          </template>
        </v-card>
      </template>
    </div>
    <slot v-else name="empty">
      <empty-state
        v-if="emptyIcon"
        :icon="emptyIcon"
        :title="emptyTitle"
        :subtitle="emptySubtitle"
      >
        <slot name="empty-actions"></slot>
      </empty-state>
    </slot>
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";

export default {
  name: "MobileDataCards",
  components: {
    EmptyState,
  },
  props: {
    headers: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    itemKey: {
      type: String,
      default: "id",
    },
    showRowNumber: {
      type: Boolean,
      default: true,
    },
    bottomPadding: {
      type: Boolean,
      default: true,
    },
    emptyIcon: {
      type: String,
      default: "",
    },
    emptyTitle: {
      type: String,
      default: "",
    },
    emptySubtitle: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    boldFields: {
      type: Array,
      default: () => [],
    },
    showFilters: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
      default: "",
    },
    sortBy: {
      type: String,
      default: "",
    },
    fieldsPerRow: {
      type: Number,
      default: 2,
      validator: (value) => [1, 2, 3, 4].includes(value),
    },
    hideLabels: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      internalSearch: "",
      internalSortBy: null,
    };
  },
  computed: {
    hasActionsSlot() {
      // Only show separate actions slot if actions header doesn't have cols (not inline)
      const actionsHeader = this.headers.find(h => h.value === 'actions');
      const hasInlineActions = actionsHeader && actionsHeader.cols && actionsHeader.mobile !== false;
      return !hasInlineActions && !!(this.$scopedSlots['item.actions'] || this.$slots['item.actions']);
    },
    sortOptions() {
      // Generate sort options from headers that don't have sortable: false or mobile: false
      return this.headers
        .filter(
          (h) =>
            h.sortable !== false &&
            h.mobile !== false &&
            h.value !== "num" &&
            h.value !== "actions" &&
            h.value !== "r_b"
        )
        .map((h) => ({
          text: h.text,
          value: h.value,
        }));
    },
    processedItems() {
      let result = [...this.items];

      // Use internal search if showFilters is enabled, otherwise use external prop
      const searchValue = this.showFilters ? this.internalSearch : this.search;
      const sortValue = this.showFilters ? this.internalSortBy : this.sortBy;

      // Filter by search (only search in headers that don't have mobile: false)
      if (searchValue) {
        const searchLower = this.toLatin(searchValue).toLowerCase();
        const searchableHeaders = this.headers.filter((h) => h.mobile !== false);
        result = result.filter((item) => {
          return searchableHeaders.some((header) => {
            const value = this.getNestedValue(item, header.value);
            return this.toLatin(String(value)).toLowerCase().includes(searchLower);
          });
        });
      }

      // Sort by column
      if (sortValue) {
        result.sort((a, b) => {
          const aVal = this.getNestedValue(a, sortValue);
          const bVal = this.getNestedValue(b, sortValue);

          let comparison = 0;
          if (typeof aVal === "number" && typeof bVal === "number") {
            comparison = aVal - bVal;
          } else {
            comparison = String(aVal).localeCompare(String(bVal));
          }

          return comparison;
        });
      }

      return result;
    },
    visibleHeaders() {
      // Filter out headers that should not be visible (like 'num' for row number)
      // Also filter out headers with mobile: false
      // Actions are included if they have cols defined (inline display)
      return this.headers.filter(
        (h) =>
          h.value !== "num" &&
          h.value !== "r_b" &&
          h.mobile !== false &&
          (h.value !== "actions" || h.cols)
      );
    },
    containerClass() {
      return this.bottomPadding ? "pb-16" : "";
    },
    isTableMode() {
      // Check if all fields fit in one row (fieldsPerRow >= total fields count)
      const totalFields = this.visibleHeaders.length + (this.showRowNumber ? 1 : 0);
      return totalFields <= this.fieldsPerRow;
    },
    tableHeaders() {
      // Build headers array for table mode (includes row number if enabled)
      const headers = [];
      if (this.showRowNumber) {
        headers.push({ text: this.$t("r_b"), value: "r_b", cols: null });
      }
      this.visibleHeaders.forEach((header) => {
        headers.push({ text: header.text, value: header.value, cols: header.cols });
      });
      return headers;
    },
  },
  methods: {
    getItemKey(item, index) {
      return item[this.itemKey] || index;
    },
    getNestedValue(obj, path) {
      if (!path) return "";
      return path.split(".").reduce((acc, part) => {
        return acc && acc[part] !== undefined ? acc[part] : "";
      }, obj);
    },
    getFieldClass(header) {
      if (this.boldFields.includes(header.value)) {
        return "font-weight-bold";
      }
      return "";
    },
    getFieldRows(item, itemIndex) {
      // Build array of all fields including row number if needed
      const fields = [];

      // Add row number as first field if enabled
      if (this.showRowNumber) {
        fields.push({
          value: "r_b",
          text: this.$t("r_b"),
          displayValue: itemIndex + 1,
          class: "font-weight-bold",
          cols: null,
        });
      }

      // Add visible headers
      this.visibleHeaders.forEach((header) => {
        let displayValue = this.getNestedValue(item, header.value);
        
        // Add suffix if specified
        if (header.suffixField) {
          const suffix = this.getNestedValue(item, header.suffixField);
          if (suffix) {
            displayValue = `${displayValue} ${suffix}`;
          }
        } else if (header.suffix) {
          displayValue = `${displayValue} ${header.suffix}`;
        }
        
        fields.push({
          value: header.value,
          text: header.text,
          displayValue: displayValue,
          class: this.getFieldClass(header),
          cols: header.cols,
        });
      });

      // Group fields based on fieldsPerRow prop or cols
      const rows = [];
      
      if (this.fieldsPerRow === null) {
        // Group by cols - each row should sum to 12
        let currentRow = [];
        let currentColSum = 0;
        
        fields.forEach((field) => {
          const fieldCols = field.cols || 6; // default to 6 if no cols
          
          if (currentColSum + fieldCols > 12) {
            // Start new row
            if (currentRow.length > 0) {
              rows.push(currentRow);
            }
            currentRow = [field];
            currentColSum = fieldCols;
          } else {
            currentRow.push(field);
            currentColSum += fieldCols;
            
            if (currentColSum === 12) {
              rows.push(currentRow);
              currentRow = [];
              currentColSum = 0;
            }
          }
        });
        
        // Push remaining fields
        if (currentRow.length > 0) {
          rows.push(currentRow);
        }
      } else {
        // Group by fieldsPerRow
        const perRow = this.fieldsPerRow;
        for (let i = 0; i < fields.length; i += perRow) {
          const row = fields.slice(i, i + perRow);
          rows.push(row);
        }
      }

      return rows;
    },
    getFieldCols(rowLength) {
      // Calculate cols based on number of items in the row
      // 12 columns total, divide by number of fields
      return Math.floor(12 / rowLength);
    },
  },
};
</script>
